import { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

// Type pour WaveSurfer avec le plugin Regions
interface WaveSurferWithRegions extends WaveSurfer {
  addRegion: (options: any) => any;
  clearRegions: () => void;
}

// Type pour les segments de prédiction
interface PredictionSegment {
  start: number;
  end: number;
  prediction: number;
}

// Props pour le hook
interface UseAudioProps {
  file: File | null;
  predictionList: PredictionSegment[] | null;
  showScores: boolean;
}

// Retour du hook
interface UseAudioReturn {
  waveformRef: React.RefObject<HTMLDivElement>;
  waveSurferRef: React.RefObject<WaveSurferWithRegions | null>;
  isPlaying: boolean;
  togglePlayPause: () => void;
  currentTime: number;
  duration: number;
  playSegment: (start: number, end: number) => void;
}

export const useAudio = ({ file, predictionList, showScores }: UseAudioProps): UseAudioReturn => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurferWithRegions | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  // Fonction pour obtenir la couleur en fonction du score
  const getColorForScore = useCallback((score: number): string => {
    if (score > 30) return 'rgba(255, 0, 0, 0.3)'; // Rouge transparent
    if (score >= 15 && score <= 30) return 'rgba(255, 165, 0, 0.3)'; // Orange transparent
    return 'rgba(0, 204, 0, 0.3)'; // Vert transparent
  }, []);

  // Initialiser WaveSurfer
  useEffect(() => {
    if (waveformRef.current && file) {
      // Charger dynamiquement le plugin Regions
      const RegionsPlugin = require('wavesurfer.js/dist/plugin/wavesurfer.regions.min.js');

      // Créer l'instance WaveSurfer avec le plugin Regions
      const waveSurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: 'rgb(29, 186, 165)',
        progressColor: 'rgb(246, 136, 7)',
        cursorColor: 'rgb(72, 78, 82)',
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 1,
        height: 100,
        barGap: 2,
        interact: true,
        plugins: [RegionsPlugin.create()],
      });

      waveSurferRef.current = waveSurfer as unknown as WaveSurferWithRegions;

      // Gérer les événements de lecture
      waveSurfer.on('play', () => setIsPlaying(true));
      waveSurfer.on('pause', () => setIsPlaying(false));
      waveSurfer.on('finish', () => setIsPlaying(false));
      waveSurfer.on('audioprocess', () => {
        setCurrentTime(waveSurfer.getCurrentTime());
      });
      waveSurfer.on('ready', () => {
        setDuration(waveSurfer.getDuration());
      });

      // Gérer le clic sur la waveform pour basculer lecture/pause
      waveSurfer.on('seek', () => {
        if (!waveSurfer.isPlaying()) {
          waveSurfer.play();
          setIsPlaying(true);
        }
      });

      const objectUrl = URL.createObjectURL(file);
      waveSurfer.load(objectUrl);

      return () => {
        waveSurfer.destroy();
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [file]);

  // Ajouter les régions pour les segments de prédiction
  useEffect(() => {
    if (waveSurferRef.current && predictionList && predictionList.length > 0 && showScores) {
      const waveSurfer = waveSurferRef.current;
      
      // Effacer les régions existantes
      waveSurfer.clearRegions();

      // Ajouter une région pour chaque segment
      predictionList.forEach((segment, index) => {
        const startTime = segment.start;
        const endTime = segment.end;
        const score = segment.prediction;
        const color = getColorForScore(score);

        const region = waveSurfer.addRegion({
          start: startTime,
          end: endTime,
          color: color,
          drag: false,
          resize: false,
          content: `Score: ${score.toFixed(2)}`,
          attributes: {
            'data-score': score.toFixed(2),
            'data-segment': index + 1
          }
        });

        // Rendre la région cliquable pour jouer le segment
        region.on('click', () => {
          if (waveSurferRef.current) {
            waveSurferRef.current.play(startTime, endTime);
            setIsPlaying(true);
          }
        });
      });
    }
  }, [predictionList, showScores, getColorForScore]);

  // Basculer lecture/pause
  const togglePlayPause = useCallback(() => {
    if (waveSurferRef.current) {
      if (waveSurferRef.current.isPlaying()) {
        waveSurferRef.current.pause();
        setIsPlaying(false);
      } else {
        waveSurferRef.current.play();
        setIsPlaying(true);
      }
    }
  }, []);

  // Jouer un segment spécifique
  const playSegment = useCallback((start: number, end: number) => {
    if (waveSurferRef.current) {
      waveSurferRef.current.play(start, end);
      setIsPlaying(true);
    }
  }, []);

  return {
    waveformRef,
    waveSurferRef,
    isPlaying,
    togglePlayPause,
    currentTime,
    duration,
    playSegment,
  };
};
